/**
 * Attachments Hook
 *
 * Custom React hook for managing bug report attachments state.
 * Provides functions for adding, removing, and validating attachments
 * with support for screenshots and uploaded files.
 *
 * @module useAttachments
 */

import { useState, useCallback } from 'react';
import {
  ClientAttachment,
  AttachmentType,
  MAX_ATTACHMENTS,
  MAX_TOTAL_SIZE,
} from '@wds/bug-reporter-types';
import { validateImageFile } from './image-utils';

/**
 * Validation error for attachments
 */
export interface AttachmentValidationError {
  /** Whether validation failed */
  hasError: boolean;
  /** Error message if validation failed */
  message?: string;
  /** Number of attachments that failed validation */
  count?: number;
}

/**
 * Result of add attachment operation
 */
export interface AddAttachmentResult {
  /** Whether attachment was added successfully */
  success: boolean;
  /** Validation error if addition failed */
  error?: AttachmentValidationError;
  /** ID of the added attachment (if successful) */
  attachmentId?: string;
}

/**
 * Hook state and return value
 */
export interface UseAttachmentsReturn {
  /** Current list of attachments */
  attachments: ClientAttachment[];
  /** Number of attachments */
  count: number;
  /** Total size of all attachments in bytes */
  totalSize: number;
  /** Add a new attachment */
  addAttachment: (file: File, type: AttachmentType) => AddAttachmentResult;
  /** Remove an attachment by ID */
  removeAttachment: (id: string) => void;
  /** Clear all attachments */
  clearAttachments: () => void;
  /** Validate all attachments against limits */
  validateAttachments: () => AttachmentValidationError;
  /** Check if another attachment can be added */
  canAddAttachment: (fileSize: number) => boolean;
}

/**
 * Custom hook for managing bug report attachments
 *
 * Manages state for file attachments with validation for:
 * - Maximum attachment count (10)
 * - Maximum file size (5MB per file)
 * - Maximum total size (25MB combined)
 * - File type validation (images only)
 *
 * @returns Hook state and functions for attachment management
 *
 * @example
 * ```tsx
 * const { attachments, addAttachment, removeAttachment, count } = useAttachments();
 *
 * // Add a file upload
 * const result = addAttachment(file, 'upload');
 * if (!result.success) {
 *   console.error(result.error?.message);
 * }
 *
 * // Add a screenshot
 * addAttachment(screenshotBlob, 'screenshot');
 *
 * // Render attachment grid
 * {attachments.map(att => (
 *   <AttachmentPreview
 *     key={att.id}
 *     attachment={att}
 *     onRemove={() => removeAttachment(att.id)}
 *   />
 * ))}
 * ```
 */
export function useAttachments(): UseAttachmentsReturn {
  const [attachments, setAttachments] = useState<ClientAttachment[]>([]);

  /**
   * Calculate total size of all attachments
   */
  const totalSize = attachments.reduce((sum, att) => sum + att.size, 0);

  /**
   * Add a new attachment to the list
   *
   * Validates file type, size, and count limits before adding.
   * Generates unique ID and creates preview URL for the file.
   *
   * @param file - File to add
   * @param type - Attachment type ('screenshot' or 'upload')
   * @returns Result object with success status and error details
   */
  const addAttachment = useCallback(
    (file: File, type: AttachmentType): AddAttachmentResult => {
      try {
        // Validate file type and size
        const validation = validateImageFile(file);
        if (!validation.valid) {
          console.error('[useAttachments] Validation failed:', validation.error);
          return {
            success: false,
            error: {
              hasError: true,
              message: validation.error,
            },
          };
        }

        // Check attachment count limit
        if (attachments.length >= MAX_ATTACHMENTS) {
          console.warn('[useAttachments] Max attachments limit reached:', MAX_ATTACHMENTS);
          return {
            success: false,
            error: {
              hasError: true,
              message: `Maximum ${MAX_ATTACHMENTS} attachments allowed`,
            },
          };
        }

        // Check total size limit
        const newTotalSize = totalSize + file.size;
        if (newTotalSize > MAX_TOTAL_SIZE) {
          console.warn('[useAttachments] Total size limit exceeded:', {
            current: totalSize,
            adding: file.size,
            max: MAX_TOTAL_SIZE,
          });
          return {
            success: false,
            error: {
              hasError: true,
              message: `Total size exceeds maximum (25 MB)`,
            },
          };
        }

        // Generate unique ID
        const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);

        // Create attachment object
        const newAttachment: ClientAttachment = {
          id,
          type,
          originalName: type === 'upload' ? file.name : undefined,
          size: file.size,
          previewUrl,
          file,
          addedAt: new Date(),
        };

        // Add to state
        setAttachments((prev) => [...prev, newAttachment]);

        console.info('[useAttachments] Attachment added:', {
          id,
          type,
          size: file.size,
          count: attachments.length + 1,
        });

        return {
          success: true,
          attachmentId: id,
        };
      } catch (error) {
        console.error('[useAttachments] Failed to add attachment:', error);
        return {
          success: false,
          error: {
            hasError: true,
            message: 'Failed to add attachment',
          },
        };
      }
    },
    [attachments, totalSize]
  );

  /**
   * Remove an attachment by ID
   *
   * Cleans up the preview URL to prevent memory leaks.
   *
   * @param id - ID of attachment to remove
   */
  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const attachment = prev.find((att) => att.id === id);
      if (attachment) {
        // Clean up preview URL to prevent memory leaks
        try {
          URL.revokeObjectURL(attachment.previewUrl);
        } catch (error) {
          console.warn('[useAttachments] Failed to revoke object URL:', error);
        }

        console.info('[useAttachments] Attachment removed:', {
          id,
          type: attachment.type,
          remainingCount: prev.length - 1,
        });
      }

      return prev.filter((att) => att.id !== id);
    });
  }, []);

  /**
   * Clear all attachments
   *
   * Cleans up all preview URLs to prevent memory leaks.
   */
  const clearAttachments = useCallback(() => {
    // Clean up all preview URLs
    attachments.forEach((attachment) => {
      try {
        URL.revokeObjectURL(attachment.previewUrl);
      } catch (error) {
        console.warn('[useAttachments] Failed to revoke object URL:', error);
      }
    });

    setAttachments([]);

    console.info('[useAttachments] All attachments cleared');
  }, [attachments]);

  /**
   * Validate all attachments against limits
   *
   * Checks for:
   * - Attachment count limit (10)
   * - Total size limit (25MB)
   *
   * @returns Validation error object
   */
  const validateAttachments = useCallback((): AttachmentValidationError => {
    // Check attachment count
    if (attachments.length > MAX_ATTACHMENTS) {
      return {
        hasError: true,
        message: `Maximum ${MAX_ATTACHMENTS} attachments allowed`,
        count: attachments.length,
      };
    }

    // Check total size
    if (totalSize > MAX_TOTAL_SIZE) {
      return {
        hasError: true,
        message: `Total size exceeds maximum (25 MB)`,
        count: attachments.length,
      };
    }

    // All validations passed
    return {
      hasError: false,
    };
  }, [attachments.length, totalSize]);

  /**
   * Check if another attachment can be added
   *
   * @param fileSize - Size of file to add in bytes
   * @returns Whether the file can be added
   */
  const canAddAttachment = useCallback(
    (fileSize: number): boolean => {
      if (attachments.length >= MAX_ATTACHMENTS) {
        return false;
      }

      if (totalSize + fileSize > MAX_TOTAL_SIZE) {
        return false;
      }

      return true;
    },
    [attachments.length, totalSize]
  );

  return {
    attachments,
    count: attachments.length,
    totalSize,
    addAttachment,
    removeAttachment,
    clearAttachments,
    validateAttachments,
    canAddAttachment,
  };
}
